import type { ButtonHTMLAttributes } from "react";

export interface GoogleButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Show a spinning state and disable the button while OAuth is in flight. */
  loading?: boolean;
  /** Override the visible label. Defaults to "Sign in with Google". */
  label?: string;
}

/**
 * GoogleButton — primary "Sign in with Google" CTA.
 *
 * White surface (Google brand requirement), 4-color logo inline, full-width.
 * Visual spec from `.btn-google` in nucce_extension_preview_v17.html (view 0).
 *
 * Tokenized: 8px radius → `rounded-md` (was Tailwind default `rounded-lg`
 * = 8px — same pixels, now a named token). Google brand requires a
 * white surface + #333 text + #ddd border, so those literals stay
 * (NOT design-system tokens — see v17-gap-analysis.md "out of WS1
 * scope: brand-locked colors"). `text-sm font-medium` is a Tailwind
 * default (not an arbitrary `text-[Npx]`); kept to preserve the
 * Google-brand button typography exactly.
 */
export function GoogleButton({
  loading = false,
  disabled,
  label = "Sign in with Google",
  type,
  className,
  ...rest
}: GoogleButtonProps) {
  const isDisabled = disabled || loading;
  const base =
    "w-full inline-flex items-center justify-center gap-2 px-4 py-[11px] " +
    "rounded-md border border-[#ddd] bg-white text-[#333] text-sm font-medium " +
    "transition-colors hover:bg-[#f5f5f5] hover:border-[#bbb] " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface " +
    "disabled:opacity-60 disabled:cursor-not-allowed";
  const merged = className ? `${base} ${className}` : base;

  return (
    <button
      type={type ?? "button"}
      disabled={isDisabled}
      aria-busy={loading}
      aria-disabled={isDisabled}
      className={merged}
      {...rest}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <title>Google</title>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      <span>{loading ? "Signing in..." : label}</span>
    </button>
  );
}
